import { useEffect, useState } from "react";
import { isAbortError, queryReport } from "../api/newReportingClient";
import { ReportResponse } from "../api/types";
import { ReportFilterValue } from "../components/NewReportFilters";
import { toQueryParams } from "./filters";

export interface EventSummaryState {
  summary: ReportResponse | null; // EventSummary — the full financial set
  hourly: ReportResponse | null; // SalesByHour — the Net-by-hour line
  topItems: ReportResponse | null; // TopItems — top products + units
  loading: boolean;
  error: string | null;
}

const EMPTY: EventSummaryState = {
  summary: null,
  hourly: null,
  topItems: null,
  loading: false,
  error: null,
};

// Event Summary composes 3 event-scoped calls in parallel. Idle until an event is picked
// (pass `null` to stay idle before Search). All 3 requests share one AbortController, so a
// change (or unmount) aborts the whole batch server-side rather than letting it run stale.
export function useEventSummary(filters: ReportFilterValue | null): EventSummaryState {
  const [state, setState] = useState<EventSummaryState>(EMPTY);
  const eventId = filters?.eventId;
  const key = filters ? JSON.stringify({ eventId, from: filters.from, to: filters.to }) : null;

  useEffect(() => {
    if (!filters || !eventId) {
      setState(EMPTY);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setState((s) => ({ ...s, loading: true, error: null }));

    Promise.all([
      queryReport(toQueryParams("EventSummary", filters), signal),
      queryReport(toQueryParams("SalesByHour", filters), signal),
      queryReport(toQueryParams("TopItems", filters, { limit: 25 }), signal),
    ])
      .then(([summary, hourly, topItems]) => {
        if (!signal.aborted) setState({ summary, hourly, topItems, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted || isAbortError(err)) return; // cancelled — leave newer state alone
        setState({
          ...EMPTY,
          error: err instanceof Error ? err.message : "Failed to load event summary",
        });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
